import React, { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Label,
  Row,
} from "reactstrap";
import { BaseInput, ErrorMessage } from "Components/ui";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import { ExpenseValidationSchema, getErrorMessage } from "utils";
import { useGetExpenseById, useUpdateExpense } from "hooks/useExpenses";
import { toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "../../constants";
import Spinners from "Components/Common/Spinner";
import CreatableSelect from "react-select/creatable";
import { useGetExpenseCategories } from "hooks/useExpenseCategory";

const EditExpense = () => {
  document.title = "Papermap | Edit Expense";

  const { mutate: updateExpense, isPending } = useUpdateExpense();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: expenseData, isLoading } = useGetExpenseById(id || "");
  const { data: expenseCategories } = useGetExpenseCategories();

  const initialValues = {
    amount: 0,
    description: "",
    date: "",
    category: { id: "", name: "" },
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: ExpenseValidationSchema,
    onSubmit: (values) => {
      console.log("Form Submitted", values);

      updateExpense(
        {
          id: expenseData.id,
          data: {
            amount: values.amount,
            description: values.description,
            date: values.date,
            category: {
              id: values.category.id || 0,
              name: values.category.name,
            },
          },
        },
        {
          onSuccess: () => {
            navigate(AppRoutes.EXPENSES);
            toast.success("Expense updated successfully.");
            formik.resetForm();
          },
        }
      );
    },
  });

  const handleCancel = () => {
    navigate(AppRoutes.EXPENSES);
  };

  useEffect(() => {
    if (expenseData) {
      formik.setValues({
        amount: expenseData.amount,
        description: expenseData.description,
        date: expenseData.date,
        category:
          expenseData.category !== null
            ? expenseData.category
            : { id: 0, value: "" },
      });
    }
  }, [expenseData]);

  if (isLoading) {
    return <Spinners />;
  }

  return (
    <React.Fragment>
      <div className={`page-content`}>
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Expenses" breadcrumbItem="Edit Expense" />

          <Form onSubmit={formik.handleSubmit} autoComplete="off">
            <Row>
              <Col xs={12}>
                {/* Basic Information */}

                <Card>
                  <CardBody>
                    {/* <CardTitle tag="h4">Basic Information</CardTitle> */}
                    <Form onSubmit={formik.handleSubmit} autoComplete="off">
                      <Row>
                        <Col sm="12">
                          <div className="mb-3">
                            <BaseInput
                              label="Description"
                              name="description"
                              type="textarea"
                              value={formik.values.description}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.description &&
                                !!formik.errors.description
                              }
                              errorMessage={getErrorMessage(
                                formik.errors.description
                              )}
                            />
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <BaseInput
                              label="Amount*"
                              name="amount"
                              type="number"
                              min="0"
                              value={formik.values.amount}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.amount && !!formik.errors.amount
                              }
                              errorMessage={getErrorMessage(
                                formik.errors.amount
                              )}
                            />
                          </div>
                        </Col>
                        <Col sm="6">
                          <div className="mb-3">
                            <label htmlFor="Category">Category</label>
                            <CreatableSelect
                              placeholder="Select or create a category"
                              isClearable
                              isLoading={isPending}
                              value={
                                formik.values.category.id
                                  ? {
                                      value: formik.values.category.id,
                                      label: formik.values.category.name,
                                    }
                                  : {
                                      value: formik.values.category.name,
                                      label: formik.values.category.name,
                                    }
                              }
                              options={
                                expenseCategories &&
                                expenseCategories.map((item) => {
                                  return { value: item.id, label: item.name };
                                })
                              }
                              onChange={(
                                selectedOption: {
                                  value: string | number;
                                  label: string;
                                } | null
                              ) => {
                                const categoryId = selectedOption
                                  ? selectedOption.value
                                  : "";
                                const categoryName = selectedOption
                                  ? selectedOption.label
                                  : "";

                                const categoryExists = expenseCategories?.some(
                                  (item) => item.id === categoryId
                                );
                                formik.setFieldValue("category", {
                                  id: categoryExists ? categoryId : "",
                                  name: categoryName,
                                });
                              }}
                            />
                            {formik.touched.category &&
                              formik.errors.category && (
                                <ErrorMessage>
                                  {typeof formik.errors.category === "string"
                                    ? formik.errors.category
                                    : formik.errors.category?.name}
                                </ErrorMessage>
                              )}
                          </div>
                        </Col>
                        <Col sm="12">
                          <div className="mb-3">
                            <Label htmlFor="date">Date</Label>
                            <Flatpickr
                              id="date"
                              name="date"
                              value={formik.values.date || undefined}
                              onChange={(date: Date[]) => {
                                const formattedDate = date.length
                                  ? date[0].toISOString().split("T")[0]
                                  : null;
                                formik.setFieldValue("date", formattedDate);
                              }}
                              options={{
                                altInput: true,
                                altFormat: "F j, Y",
                                dateFormat: "Y-m-d",
                              }}
                              className="form-control"
                            />

                            {formik.touched.date && formik.errors.date && (
                              <ErrorMessage>
                                {getErrorMessage(formik.errors.date)}
                              </ErrorMessage>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {/* Button Section */}
            <div className="d-flex justify-content-end mt-3">
              <Button
                type="submit"
                color="primary"
                className="w-20 me-1"
                style={{ width: "20%" }}
              >
                {isPending ? "Submitting ..." : "Submit"}
              </Button>
              <Button
                color="secondary"
                className="w-20 ms-1"
                style={{ width: "20%" }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EditExpense;
